<template>
    <pc-table
        v-bind:items="envelopes"
        v-bind:columns="columns"
        v-bind:initial-sort="initialSort"
        v-bind:empty-text="emptyTable"
        key-prop="EnvelopeId"
        hide-search>
        <template v-slot:Date="envelope">
            <div>
                <span
                    v-if="wideView && envelope.IsInvisibleToProjectOwner"
                    class="text-primary fw-bold"
                    >*</span
                >
                <span>{{ envelope.Date }}</span>
            </div>
        </template>
        <template v-slot:Status="envelope">
            <span v-bind:class="getEnvelopeStatusTextBubbleClass(envelope.Status.Code)">
                {{ envelope.Status.Display }}
            </span>
        </template>
        <template v-slot:actionsRow="envelope">
            <pc-loader
                v-bind:loading="loading"
                v-bind:message="actionLoadingMessage">
                <div
                    v-if="isAdminPage"
                    class="mb-3">
                    <pc-button
                        v-if="showManageRequestButton(envelope)"
                        v-bind:disabled="!isProjectModuleRegistered"
                        v-on:click="onManageRequest(envelope)">
                        {{ manageRequestButtonLabel }}
                    </pc-button>
                    <pc-button
                        v-if="showDiscardButton(envelope)"
                        v-on:click="onDiscard(envelope)">
                        {{ discardButtonLabel }}
                    </pc-button>
                    <pc-button
                        v-if="showStartOverButton(envelope)"
                        v-bind:disabled="!isProjectModuleRegistered"
                        v-on:click="onStartOver(envelope)">
                        {{ startOverButton }}
                    </pc-button>
                </div>
                <envelope-status
                    v-bind:envelope="envelope"
                    v-bind:show-certificate="isAdminPage"
                    v-bind:download-completed-item-fn="downloadCompletedItemFn"
                    v-bind:get-content-library-item-by-id-fn="getContentLibraryItemByIdFn"
                    class="mb-3" />
            </pc-loader>
        </template>
    </pc-table>
</template>
<script>
    import Vuex from "vuex";
    import { DIALOG_RESULT } from "Components/pc-dialog.vue";
    import { isModuleRegistered } from "SharedStores/util";

    import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
    import { PROJECT_NAMESPACE, PROJECT_TYPES } from "SharedStores/Project/projectStore";
    import { PROJECT_FORM_NAMESPACE, PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_FORM_CONTROLLER_TYPES } from "Forms/Components/ProjectForm/projectFormControllerStore";
    import { downloadFile } from "Util/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS as ENV_KEYS, NAMESPACE as ENV_NAMESPACE } from "Forms/Components/Elements/i18n/eSignature.i18n";
    import { KEYS, NAMESPACE } from "./i18n/advancedESignatureCapsuleTable.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";

    import {
        isEnvelopeStatusTerminal,
        getEnvelopeStatusTextBubbleClass,
        getEnvelopeSignatureTypeLabel,
        ESIGNATURE_CANCEL_EXPLANATION_TYPE,
        WET_SIGN_VOID_REASON
    } from "ESignatures/eSignatureConstants";
    import { getCorrectionsListFromContactInfoUpdateDict } from "ESignatures/ManageRequest/manageRequestUtil";
    import envelopeStatus from "ESignatures/EnvelopeStatus/envelopeStatus.vue";
    import manageRequestDialog from "Forms/Components/Elements/envelope/manageRequestDialog.vue";
    import startOverEnvelopeDialog from "Forms/Components/Elements/envelope/startOverEnvelopeDialog.vue";

    export default {
        components: {
            envelopeStatus
        },
        props: {
            // Need to have this passed in rather than relying on projectStore because landing
            // page doesn't use project store
            envelopes: { type: Array, required: true },
            isAdminPage: { type: Boolean, required: true },
            wideView: { type: Boolean, required: true },
            isReadOnlyUser: { type: Boolean, required: true },
            initialSort: { type: Array, required: true },
            programId: { type: String, required: true },
            projectId: { type: String, required: true },
            /** Must be object of type ClientESignatureProviderCapabilities */
            providerCapabilities: { type: Object, required: true },
            contentLibraryItemsById: { type: Object, required: false, default: () => {} }
        },
        emits: ["envelope-updated", "envelope-voided"],
        data() {
            return {
                loading: false,
                // Name cannot be loadingMessage, overlaps with common i18n key
                actionLoadingMessage: null
            };
        },
        computed: {
            isProjectFormModuleRegistered() {
                return isModuleRegistered(PROJECT_FORM_NAMESPACE, this.$store.state);
            },
            eSignaturesController() {
                return this.$serviceControllers.ESignatures;
            },
            attachmentController() {
                return this.$serviceControllers.AttachmentsCapsule;
            },
            downloadCompletedItemFn() {
                return this.isAdminPage ? this.viewCompletedEnvelopeAttachment : this.downloadAttachment;
            },
            getContentLibraryItemByIdFn() {
                return this.isAdminPage ? this.getContentLibraryItemById : this.getContentLibraryItemByIdNoStore;
            },
            isProjectModuleRegistered() {
                return isModuleRegistered(PROJECT_NAMESPACE, this.$store.state);
            },
            ...Vuex.mapGetters({
                getContentLibraryItemById: PROGRAM_TYPES.getters.getContentLibraryItemById,
                getUserLoginIdToUserInfo: PROJECT_FORM_TYPES.getters.getUserLoginIdToUserInfo,
                dataFieldValueFromId: PROJECT_TYPES.getters.dataFieldValueFromId
            }),
            emptyTable() {
                return this.emptyTableText;
            },
            columns() {
                return [
                    {
                        header: this.createdTimestampHeader,
                        property: "CreatedTimestamp",
                        orderable: true,
                        visible: true,
                        sortValue(row) {
                            return row.CreatedTimestamp.Ticks;
                        },
                        displayValue(value) {
                            return value.Display;
                        }
                    },
                    {
                        header: this.envelopeNameHeader,
                        property: "Definition",
                        orderable: true,
                        visible: true,
                        sortValue(row) {
                            return (row.Definition && row.Definition.Name) || "";
                        },
                        searchValue(row) {
                            return (row.Definition && row.Definition.Name) || "";
                        },
                        displayValue(defInfo) {
                            return defInfo.Name;
                        }
                    },
                    {
                        header: this.signatureTypeHeader,
                        property: "IsWetSign",
                        orderable: true,
                        visible: true,
                        sortValue: function (row) {
                            return getEnvelopeSignatureTypeLabel(this.$app, row.IsWetSign);
                        }.bind(this),
                        searchValue: function (row) {
                            return getEnvelopeSignatureTypeLabel(this.$app, row.IsWetSign);
                        }.bind(this),
                        displayValue: function (isWetSign) {
                            return getEnvelopeSignatureTypeLabel(this.$app, isWetSign);
                        }.bind(this)
                    },
                    {
                        header: this.statusTimestampHeader,
                        property: "StatusTimestamp",
                        orderable: true,
                        visible: true,
                        sortValue(row) {
                            return row.StatusTimestamp.Ticks;
                        },
                        displayValue(value) {
                            return value.Display;
                        }
                    },
                    {
                        header: this.statusHeader,
                        property: "Status",
                        orderable: true,
                        visible: true
                    }
                ];
            },
            ...mapI18n(ENV_KEYS, ENV_NAMESPACE),
            ...mapI18n(KEYS, NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE)
        },
        methods: {
            showStartOverButton(envelope) {
                return (
                    this.$app.LoggedInRole.RolePrivileges.ProjectAdminStartOverESignature &&
                    isEnvelopeStatusTerminal(envelope.Status.Code)
                );
            },
            showManageRequestButton(envelope) {
                return (
                    this.$app.LoggedInRole.RolePrivileges.ProjectAdminManageESignatureRequest &&
                    !isEnvelopeStatusTerminal(envelope.Status.Code) &&
                    !envelope.IsWetSign
                );
            },
            showDiscardButton(envelope) {
                return (
                    this.$app.LoggedInRole.RolePrivileges.ProjectAdminManageESignatureRequest &&
                    !isEnvelopeStatusTerminal(envelope.Status.Code) &&
                    envelope.IsWetSign
                );
            },
            onManageRequest(envelope) {
                if (!this.isProjectFormModuleRegistered) {
                    throw "Manage request requires project form module.";
                }
                this.$app.showDialog({
                    large: true,
                    title: this.manageRequestTitle,
                    customComponent: manageRequestDialog,
                    customComponentOptions: {
                        envelope: envelope,
                        allowManualCorrections: true,
                        providerCapabilities: this.providerCapabilities,
                        getDataFieldValueByIdFn: this.dataFieldValueFromId,
                        getUserInfoByLoginIdFn: this.getUserLoginIdToUserInfo
                    },
                    customComponentListeners: {
                        "cancel-all": (voidData) => {
                            this.$app.closeDialog();
                            this.voidEnvelope(envelope, voidData);
                        },
                        "resend-notifications": (contactInfoUpdatesByRecipientId) => {
                            this.$app.closeDialog();
                            this.resendNotifications(envelope, contactInfoUpdatesByRecipientId);
                        }
                    },
                    buttons: [
                        {
                            text: this.closeText,
                            primary: true,
                            result: DIALOG_RESULT.cancel
                        }
                    ]
                });
            },
            async onDiscard(envelope) {
                const title = this.$app.res({
                    namespace: NAMESPACE,
                    key: KEYS.discardEnvelopeTitle,
                    values: { envelopeName: envelope.Definition.Name }
                });
                if (!(await this.$app.showConfirmationDialog(title, this.discardEnvelopeMessage))) {
                    return;
                }

                this.voidEnvelope(envelope, {
                    voidReason: WET_SIGN_VOID_REASON,
                    explanationType: ESIGNATURE_CANCEL_EXPLANATION_TYPE.WetSign
                });
            },
            async onStartOver(envelope) {
                const result = await this.$app.showDialog({
                    title: this.hideEnvelopeConfirmationHeader,
                    customComponent: startOverEnvelopeDialog,
                    customComponentOptions: {
                        envelope: envelope
                    },
                    buttons: [
                        {
                            text: this.cancelText,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            text: this.startOverButton,
                            primary: true,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });

                if (result === DIALOG_RESULT.ok) {
                    this.loading = true;
                    this.loadMessage = this.startingOverLoadingText;
                    try {
                        await this.startOverEnvelope({ envelopeId: envelope.EnvelopeId });
                        this.$emit("envelope-voided", envelope);
                    } finally {
                        this.loading = false;
                    }
                }
            },
            async resendNotifications(envelope, contactInfoUpdatesByRecipientId) {
                if (this.loading) {
                    return;
                }

                this.loading = true;
                this.actionLoadingMessage = this.resendLoadingText;
                const corrections = getCorrectionsListFromContactInfoUpdateDict(contactInfoUpdatesByRecipientId);

                try {
                    const result = await this.eSignaturesController.ResendEnvelopeNotificationEmailsV2({
                        programId: this.programId,
                        projectId: this.projectId,
                        envelopeId: envelope.EnvelopeId,
                        corrections
                    });

                    const updatedEnvelope = result.Data.Envelope;
                    this.$emit("envelope-updated", updatedEnvelope);
                } finally {
                    this.loading = false;
                }
            },
            async voidEnvelope(envelope, voidData) {
                if (!this.isProjectFormModuleRegistered) {
                    throw "Void envelope requires project form module.";
                }

                if (this.loading) {
                    return;
                }

                this.loading = true;
                this.actionLoadingMessage = this.voidLoadingText;
                try {
                    await this.voidSignaturesV2({
                        envelopeId: envelope.EnvelopeId,
                        ...voidData
                    });

                    this.$emit("envelope-voided", envelope);
                } finally {
                    this.loading = false;
                }
            },
            async downloadAttachment({ attachmentId }) {
                const result = await this.attachmentController.GetDownloadAttachmentUrl({
                    programId: this.programId,
                    attachmentId: attachmentId
                });
                downloadFile(result.Data);
            },
            getContentLibraryItemByIdNoStore(contentItemId) {
                return this.contentLibraryItemsById[contentItemId] ?? {};
            },
            getEnvelopeStatusTextBubbleClass,
            ...Vuex.mapActions({
                viewCompletedEnvelopeAttachment: PROJECT_FORM_CONTROLLER_TYPES.actions.viewCompletedEnvelopeAttachment,
                voidSignaturesV2: PROJECT_FORM_TYPES.actions.voidSignaturesV2,
                startOverEnvelope: PROJECT_FORM_TYPES.actions.startOverEnvelope
            })
        }
    };
</script>
<style lang="scss" scoped>
    // Override normal button width to save horizontal real-estate
    .esignature-action-button {
        min-width: 0rem !important;
    }
</style>
